import { vec3, quat } from 'gl-matrix';

export type PhysicsMode = 'gravity-only' | 'dynamic';

export interface PhysicsBodyOptions {
  gravity?: number;
  terminalVelocity?: number;
  groundingEpsilon?: number;
  mode?: PhysicsMode;
  surfaceOffset?: number;
  restitution?: number;
  mass?: number;
  inverseInertiaDiag?: vec3;
  centerOfMassOffset?: vec3;
  localMin?: vec3;
  localMax?: vec3;
  angularDamping?: number;
  initialAngularVelocity?: vec3;
}

export class PhysicsBody {
  public velocity: vec3;
  public gravity: number;
  public terminalVelocity: number;
  public groundingEpsilon: number;
  public grounded: boolean;
  public surfaceHeight: number | null;
  public minFloorHeight: number | null;
  public mode: PhysicsMode;
  public surfaceOffset: number;
  public restitution: number;
  public previousPosition: vec3;

  // Rotational dynamics (null inverseInertiaDiag = no rotation, backward compat)
  public orientation: quat;
  public angularVelocity: vec3;
  public mass: number;
  public inverseInertiaDiag: vec3 | null;
  public centerOfMassOffset: vec3;
  public localMin: vec3;
  public localMax: vec3;
  public angularDamping: number;

  constructor(
    public position: vec3 | number[],
    options?: PhysicsBodyOptions,
  ) {
    this.velocity = vec3.create();
    this.gravity = options?.gravity ?? -20;
    this.terminalVelocity = options?.terminalVelocity ?? -50;
    this.groundingEpsilon = options?.groundingEpsilon ?? 0.05;
    this.grounded = false;
    this.surfaceHeight = null;
    this.minFloorHeight = null;
    this.mode = options?.mode ?? 'gravity-only';
    this.surfaceOffset = options?.surfaceOffset ?? 0;
    this.restitution = options?.restitution ?? 0;
    this.previousPosition = vec3.fromValues(
      position[0] as number,
      position[1] as number,
      position[2] as number,
    );

    this.orientation = quat.create();
    this.angularVelocity = options?.initialAngularVelocity
      ? vec3.clone(options.initialAngularVelocity)
      : vec3.create();
    this.mass = options?.mass ?? 1;
    this.inverseInertiaDiag = options?.inverseInertiaDiag
      ? vec3.clone(options.inverseInertiaDiag)
      : null;
    this.centerOfMassOffset = options?.centerOfMassOffset
      ? vec3.clone(options.centerOfMassOffset)
      : vec3.create();
    this.localMin = options?.localMin ? vec3.clone(options.localMin) : vec3.create();
    this.localMax = options?.localMax ? vec3.clone(options.localMax) : vec3.create();
    this.angularDamping = options?.angularDamping ?? 0.98;
  }

  public integrate(dt: number): void {
    vec3.set(
      this.previousPosition,
      this.position[0] as number,
      this.position[1] as number,
      this.position[2] as number,
    );

    if (this.mode === 'dynamic') {
      this.position[0] += this.velocity[0] * dt;
      this.position[2] += this.velocity[2] * dt;
    }

    // Apply gravity — skip when grounded to prevent micro-oscillation.
    // When surface height drops (stairs down), floor check sets grounded=false
    // and gravity kicks in naturally for a smooth fall.
    if (!this.grounded) {
      this.velocity[1] += this.gravity * dt;
      if (this.velocity[1] < this.terminalVelocity) {
        this.velocity[1] = this.terminalVelocity;
      }
      this.position[1] += this.velocity[1] * dt;
    }

    // Hard floor clamp for falling bodies — prevents dropping below nav surface.
    // Skipped when grounded so the floor check lerp can smooth upward transitions.
    if (this.minFloorHeight !== null && !this.grounded) {
      let minY = (this.minFloorHeight as number) + this.surfaceOffset;
      if ((this.position[1] as number) < minY) {
        this.position[1] = minY;
        if (this.velocity[1] < 0) this.velocity[1] = 0;
      }
    }

    // Floor clamp — prevent bodies from tunneling below the known surface.
    // For restitution=0 bodies: clamp and ground (no bouncing).
    // For restitution>0 bodies (projectiles): clamp only, bounce logic runs externally.
    if (this.surfaceHeight !== null) {
      let restHeight = (this.surfaceHeight as number) + this.surfaceOffset;
      if ((this.position[1] as number) <= restHeight) {
        if (this.restitution === 0) {
          // Smooth upward transitions (stairs) for navigation-bound bodies,
          // snap instantly for falling bodies landing on surfaces
          if (this.minFloorHeight !== null && this.grounded) {
            let gap = restHeight - (this.position[1] as number);
            if (gap > 0.01) {
              this.position[1] = (this.position[1] as number) + gap * Math.min(1, 15 * dt);
            } else {
              this.position[1] = restHeight;
            }
          } else {
            this.position[1] = restHeight;
          }
          this.velocity[1] = 0;
          this.grounded = true;
        } else {
          // Bouncing body — clamp position but don't zero velocity or ground.
          // The external bounce handler will reflect velocity properly.
          this.position[1] = restHeight;
        }
      } else if (this.restitution === 0) {
        this.grounded = false;
      }
    }

    // Angular integration (quaternion derivative method)
    if (this.inverseInertiaDiag !== null) {
      let ax = this.angularVelocity[0] * dt * 0.5;
      let ay = this.angularVelocity[1] * dt * 0.5;
      let az = this.angularVelocity[2] * dt * 0.5;

      // dq = [ax, ay, az, 0] * orientation
      let ox = this.orientation[0],
        oy = this.orientation[1],
        oz = this.orientation[2],
        ow = this.orientation[3];

      this.orientation[0] += ax * ow + ay * oz - az * oy;
      this.orientation[1] += ay * ow + az * ox - ax * oz;
      this.orientation[2] += az * ow + ax * oy - ay * ox;
      this.orientation[3] += -(ax * ox + ay * oy + az * oz);

      quat.normalize(this.orientation, this.orientation);

      // Angular damping
      vec3.scale(this.angularVelocity, this.angularVelocity, this.angularDamping);
    }
  }

  /**
   * Compute the distance from the body position (mesh origin) down to the lowest
   * Y of the oriented bounding box. Uses the actual local-space AABB corners
   * rotated by the current orientation.
   */
  public getGroundClearance(): number {
    if (this.inverseInertiaDiag === null) return this.surfaceOffset;

    let qx = this.orientation[0],
      qy = this.orientation[1],
      qz = this.orientation[2],
      qw = this.orientation[3];

    // Row 1 (Y components) of the rotation matrix from quaternion
    let r10 = 2 * (qx * qy - qw * qz);
    let r11 = 1 - 2 * (qx * qx + qz * qz);
    let r12 = 2 * (qy * qz + qw * qx);

    // Find the minimum Y across all 8 corners of the local AABB, rotated.
    // Corner Y = r10*cx + r11*cy + r12*cz where (cx,cy,cz) is a corner.
    // Min Y is achieved by picking min or max for each axis based on sign of r1x.
    let minY =
      (r10 < 0 ? r10 * this.localMax[0] : r10 * this.localMin[0]) +
      (r11 < 0 ? r11 * this.localMax[1] : r11 * this.localMin[1]) +
      (r12 < 0 ? r12 * this.localMax[2] : r12 * this.localMin[2]);

    // Clearance = how far origin must be above the surface so the lowest point
    // is at the surface. If minY is negative (below origin), clearance is positive.
    return -minY;
  }

  /**
   * Compute the distance from the body position (mesh origin) up to the highest
   * Y of the oriented bounding box. Used for ceiling collision.
   */
  public getCeilingClearance(): number {
    if (this.inverseInertiaDiag === null) return this.surfaceOffset;

    let qx = this.orientation[0],
      qy = this.orientation[1],
      qz = this.orientation[2],
      qw = this.orientation[3];

    let r10 = 2 * (qx * qy - qw * qz);
    let r11 = 1 - 2 * (qx * qx + qz * qz);
    let r12 = 2 * (qy * qz + qw * qx);

    // Max Y: pick the corner that maximizes r10*cx + r11*cy + r12*cz
    let maxY =
      (r10 > 0 ? r10 * this.localMax[0] : r10 * this.localMin[0]) +
      (r11 > 0 ? r11 * this.localMax[1] : r11 * this.localMin[1]) +
      (r12 > 0 ? r12 * this.localMax[2] : r12 * this.localMin[2]);

    return maxY;
  }
}
